import { useCallback, useEffect, useRef } from "react";
import { runStore } from "../store/runStore.js";
import {
  customEventPopoverClosed,
  customEventPopoverEdited,
  customEventPopoverOpened,
  customEventPopoverRejected,
} from "../lib/forecast/reducer.js";
import { resolveTriggerInstant } from "../lib/forecast/branchTrigger.js";

/**
 * The "add custom event" popover, as a surface of one forecast's run.
 *
 * It used to be app-wide state with no owner, so its Branch button branched whatever was
 * selected when it was pressed: a rejection that reopened it after the user had moved to
 * another forecast retried the text there. It now lives in that run's slot of the store
 * (`customEventPopovers`), shows only while that run is selected, and every attempt names its
 * owning run explicitly; a rejection comes back carrying that run, and reopens there.
 *
 * @param {{
 *   active: {id?: string}|null,
 *   handleCustomEventBranch: Function,
 *   popoverRunId: string|null,
 *   popoverOpen: boolean,
 * }} deps `popoverRunId` / `popoverOpen`: the run whose popover is on screen, and whether one is.
 */
export function useCustomEventPopover({
  active,
  handleCustomEventBranch,
  popoverRunId,
  popoverOpen,
}) {
  const activeId = active?.id ?? null;
  // The popover's inputs stay uncontrolled: what is typed is not written to the store per
  // keystroke. It is kept on the run once, when the popover leaves the screen because another
  // forecast was selected, so it is still there when that forecast is selected again.
  const descriptionRef = useRef(null);
  const dateRef = useRef(null);
  useEffect(() => {
    if (!popoverOpen || !popoverRunId) return undefined;
    const description = descriptionRef.current;
    const date = dateRef.current;
    return () => {
      if (!description && !date) return;
      // A no-op when the popover was closed rather than hidden: its slot is already gone.
      runStore.dispatch(
        customEventPopoverEdited(popoverRunId, {
          ...(description ? { description: description.value } : {}),
          ...(date?.value ? { date: date.value } : {}),
        }),
      );
    };
  }, [popoverRunId, popoverOpen]);

  // The timeline double-click: the popover opens on the forecast whose timeline it is.
  const openCustomEventPopover = useCallback(
    (placement) => {
      if (activeId) runStore.dispatch(customEventPopoverOpened(activeId, placement));
    },
    [activeId],
  );

  const closeCustomEventPopover = useCallback((runId) => {
    if (runId) runStore.dispatch(customEventPopoverClosed(runId));
  }, []);

  // `ownerRunId` is the run whose popover this is. `day` is what the date picker holds
  // (YYYY-MM-DD); `clickIso` the instant the user double-clicked at, when this came from the
  // timeline. resolveTriggerInstant folds them into the one placement instant the fork builder
  // needs — a day alone becomes UTC midnight, which parks the node at the start of its day and
  // makes a same-day trigger read as already past.
  //
  // The popover is NOT closed here: handleCustomEventBranch closes it once its guards pass (or
  // fail), and leaves it open — typed text intact — when the deep-engine confirm is declined or
  // the tier is not yet knowable. When the text cannot become a contract (so no branch is made),
  // the owner's popover reopens with the text and the reason, unless one is open there again.
  const submitCustomTrigger = useCallback(
    (ownerRunId, rawDesc, day, clickIso = null, popover = null) => {
      if (!ownerRunId || !rawDesc.trim()) return undefined;
      return handleCustomEventBranch(
        rawDesc.trim(),
        resolveTriggerInstant({ day, clickIso }) || day,
        {
          ownerRunId,
          // The depth picked in the trigger box; Quick unless the user chose Deep.
          tier: popover?.tier || "quick",
          onRejected: (rejection) =>
            runStore.dispatch(
              customEventPopoverRejected(rejection.ownerRunId, {
                ...popover,
                date: day,
                description: rawDesc,
                problem: rejection.message,
              }),
            ),
        },
      );
    },
    [handleCustomEventBranch],
  );

  // A chat-typed trigger (`add_event`) branches the forecast the chat message was sent on,
  // which the chat intent captures before its classifier await and hands over as `ownerRunId`.
  // A rejection opens that run's popover — centred, since no click placed it — with the text
  // and the reason.
  const chatCustomEventBranch = useCallback(
    (desc, placement, { ownerRunId, tier } = {}) =>
      handleCustomEventBranch(desc, placement, {
        ownerRunId,
        tier,
        onRejected: (rejection) =>
          runStore.dispatch(
            customEventPopoverRejected(rejection.ownerRunId, {
              screenX: Math.round(window.innerWidth / 2),
              screenY: Math.round(window.innerHeight / 3),
              date: String(placement).slice(0, 10),
              dateTime: String(placement),
              description: desc,
              problem: rejection.message,
              // A retry from the reopened popover runs at the depth the chat asked for.
              tier,
            }),
          ),
      }),
    [handleCustomEventBranch],
  );

  return {
    descriptionRef,
    dateRef,
    openCustomEventPopover,
    closeCustomEventPopover,
    submitCustomTrigger,
    chatCustomEventBranch,
  };
}
